// Marketplace v1 (M1e) — the FIXED subdomain/id of the marketplace host tenant
// (see backend scripts/seed_marketplace_tenant.py). The host is not resolved as
// a tenant subdomain for ROUTING (getTenantSubdomain returns null there so the
// marketplace front door renders), but AUTHENTICATION on the host must still
// target THIS tenant — the admin shell + login use it.
import { getCustomDomainMapping } from './customDomain';
export const MARKETPLACE_HOST_SUBDOMAIN = 'marketplace';
export const MARKETPLACE_HOSTS = Object.freeze([
  'marketplace.plateos.io',
  'getyofo.com',
  'www.getyofo.com',
]);

/**
 * Marketplace v1 (M1e) — is this host the MARKETPLACE FRONT DOOR
 * (marketplace.plateos.io)? When true the app renders the cross-restaurant
 * /marketplace front instead of resolving a per-restaurant tenant. A
 * `?marketplace=1` / sessionStorage override (mirroring `?main=1`) lets the
 * founder verify it on preview/local hosts, where every host maps to staging.
 * Kept separate from getTenantSubdomain so a real tenant never trips it.
 */
export const isMarketplaceHost = () => {
  if (getCustomDomainMapping()) return false;
  try {
    const qp = new URLSearchParams(window.location.search);
    if (qp.get('marketplace') === '1') sessionStorage.setItem('plateos_force_marketplace', '1');
    if (qp.get('marketplace') === '0') sessionStorage.removeItem('plateos_force_marketplace');
    if (sessionStorage.getItem('plateos_force_marketplace') === '1') return true;
  } catch { /* noop */ }
  if (window.location.hostname === 'marketplace.plateos.io') return true;
  return MARKETPLACE_HOSTS.includes(window.location.hostname.toLowerCase());
};

// Keep customer-facing links canonical on the dedicated YOFO hosts while
// preserving /marketplace as the preview/fallback surface on plateos.io.
export const marketplaceHomePath = () => (isMarketplaceHost() ? '/' : '/marketplace');
export const marketplaceBrowsePath = () => (
  isMarketplaceHost() ? '/browse' : '/marketplace/browse'
);

/**
 * Detect if the current hostname is a tenant subdomain.
 * Returns the subdomain string or null for main domain / preview.
 */
export const getTenantSubdomain = () => {
  const custom = getCustomDomainMapping();
  if (custom) return custom.subdomain;
  const hostname = window.location.hostname;

  // Marketplace v1 (M1e) — the marketplace FRONT DOOR host is NEVER a tenant.
  // Resolve it before the .plateos.io label check so "marketplace" can't be
  // misread as a tenant subdomain (App.js also short-circuits on isMarketplaceHost).
  if (isMarketplaceHost()) return null;

  // PREVIEW ESCAPE HATCH: `?main=1` forces the MAIN-domain experience
  // (marketing landing page etc.) on preview hosts, where the hostname
  // otherwise always maps to the staging tenant. Sticky for the session;
  // `?main=0` clears it.
  try {
    const qp = new URLSearchParams(window.location.search);
    if (qp.get('main') === '1') sessionStorage.setItem('plateos_force_main', '1');
    if (qp.get('main') === '0') sessionStorage.removeItem('plateos_force_main');
    if (sessionStorage.getItem('plateos_force_main') === '1') return null;
  } catch { /* noop */ }

  // Direct plateos.io subdomain check (highest priority - PRODUCTION)
  if (hostname.endsWith('.plateos.io')) {
    const subdomain = hostname.replace('.plateos.io', '');
    // "api" is the API endpoint, NOT a tenant
    if (subdomain && subdomain !== 'www' && subdomain !== 'api') {
      console.log('Detected tenant subdomain:', subdomain);
      return subdomain;
    }
  }

  // Main domains that should NOT show tenant UI
  const mainDomains = [
    'plateos.io', 'www.plateos.io', 'getyofo.com', 'www.getyofo.com',
    'localhost', '127.0.0.1',
  ];
  if (mainDomains.includes(hostname)) return null;

  // STAGING/PREVIEW ENVIRONMENT: Any *.preview.emergentagent.com URL maps to staging tenant
  // This allows any forked session or preview deployment to work with the staging tenant
  if (hostname.endsWith('.preview.emergentagent.com')) {
    console.log('Detected preview environment, using staging tenant');
    return 'staging';
  }

  // Other emergent URLs without specific handling
  if (hostname.includes('emergent')) {
    return null;
  }

  return null;
};
